import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { formatAdminPermissionLabel } from "./adminPermissionCopy";
import { IdentitySessionSignInButton } from "./IdentitySessionSignInButton";
import {
  buildRouteAccessCheckFailureDetails,
  formatApiError,
  isForbiddenIdentityError,
  resolveBypassMenuVisibility,
  resolveBypassPermissionKeys,
  ROUTE_ACCESS_CHECK_FAILED_ERROR_CODE,
  shouldCheckMenuVisibility,
  shouldCheckPermission,
} from "./permissionProtectedRouteHelpers";
import {
  formatAvailabilityText,
  formatRoleLabels,
  formatVisibleAccessLabels,
  MENU_VISIBILITY_COPY_LABELS,
  RouteGateAccessSummary,
  RouteGateCard,
  RouteGatePageShell,
  RouteGateShellSurface,
} from "./RouteGateUi";
import {
  IdentityReconnectContext,
  TemplateEditorRecoveryContext,
  type TemplateEditorRecoveryOwner,
} from "./templateEditorRecovery";
import {
  isDefiniteIdentitySessionError,
  useIdentityReconnect,
} from "./useIdentityReconnect";

import { useAuthState } from "@/auth/helloCashflowDomain/useAuthState";
import {
  isOutageRelatedAccessFailure,
  useApiHealthRecovery,
} from "@/components/ui/ApiHealthRecoveryContext";
import { adminUiCopy } from "@/copy/adminUiCopy";
import {
  useCreateAdminV2ApprovalMutation,
  useGetAdminV2MeQuery,
  useGetAdminV2RevenueAccessContextQuery,
} from "@/store/api/baseApi";
import type {
  AdminV2RevenueAccessContext,
  AdminV2RevenueMenuVisibilityKey,
} from "@/types/adminV2";
import { resolveAdminRevenueAccessScope } from "@/utils/adminRevenueAccessScope";
import {
  getE2eBypassMenuVisibility,
  getE2eBypassPermissionKeys,
  isAdminE2eBypassEnabled,
  isAdminV2FoundationsEnabled,
} from "@/utils/featureFlags";
import { generateCorrelationId } from "@/utils/logging/correlationId";
import { isRevenueMenuVisibilityEnabled } from "@/utils/revenueAccessVisibility";
import { VNextButton, VNextNotice } from "@/vnext/primitives";
import { RecoveryNoticesProvider } from "@/vnext/shared/RecoveryNoticesContext";
import "@/vnext/theme/vnext.css";

interface PermissionProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredPermissionFallbacks?: readonly string[];
  requiredMenuVisibility?: AdminV2RevenueMenuVisibilityKey;
  preserveOpenTemplateEditor?: boolean;
}

const ROUTE_ACCESS_APPROVAL_REQUEST_TYPE = "admin.route.access";
const ROUTE_ACCESS_APPROVAL_TARGET_TYPE = "admin_route";
const APPROVALS_QUEUE_ROUTE_PATH = "/settings/approvals";
const FEATURE_FLAGS_ROUTE_PATH = "/settings/feature-flags";
const ENTITLEMENTS_ROUTE_PATH = "/settings/entitlements";

const loadingView = (
  // Keep the recovery shell mounted across pending/error transitions. Its
  // access subscriptions must not remount and retry a rejected bootstrap read.
  // The permission gate still withholds protected children until it succeeds.
  <RouteGatePageShell
    subtitle={adminUiCopy.routeAccess.loadingMessage}
    title={adminUiCopy.routeAccess.loadingTitle}
  >
    <RouteGateShellSurface>
      <RouteGateCard
        alertMessage={adminUiCopy.routeAccess.loadingMessage}
        alertSeverity="warning"
        embedded
        maxWidth={520}
        title={adminUiCopy.routeAccess.loadingTitle}
      >
        <div
          aria-live="polite"
          className="vnext-blocking-card__status"
          role="status"
        >
          <span aria-hidden="true" className="vnext-blocking-card__spinner" />
          {adminUiCopy.routeAccess.loadingStatus}
        </div>
      </RouteGateCard>
    </RouteGateShellSurface>
  </RouteGatePageShell>
);

const ROUTE_ACCESS_CHECK_TIMEOUT_MS = 12_000;
const ROUTE_ACCESS_LOG_DEDUPE_WINDOW_MS = 1_000;
const EMPTY_PERMISSION_FALLBACKS: readonly string[] = [];
// Dev StrictMode remounts can replay the same route-evaluation log back-to-back.
const recentRouteAccessEventTimestamps = new Map<string, number>();
const getRouteAccessLogTimestamp = () => Date.now();

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "PermissionProtectedRoute",
);

export { formatApiError };

const hasRequiredRoutePermission = (
  permissionKeys: ReadonlySet<string> | readonly string[],
  requiredPermission: string | undefined,
  requiredPermissionFallbacks: readonly string[] = [],
): boolean => {
  if (!requiredPermission) {
    return true;
  }

  const candidateKeys = [requiredPermission, ...requiredPermissionFallbacks];

  const grantedPermissionKeys = new Set(permissionKeys);

  return candidateKeys.some((permissionKey) =>
    grantedPermissionKeys.has(permissionKey),
  );
};

const buildRouteAccessReason = (args: {
  routePath: string;
  requiredPermission?: string;
  requiredMenuVisibility?: AdminV2RevenueMenuVisibilityKey;
}) => {
  // Approval requests are most useful when they capture "why this route is blocked" in the moment.
  // Keep this reason user-safe (no internal error details) and stable (so the approvals queue is scannable).
  const details: string[] = [];
  if (args.requiredPermission) {
    details.push(
      `missing access ${formatAdminPermissionLabel(args.requiredPermission)}`,
    );
  }
  if (args.requiredMenuVisibility) {
    details.push(`visibility gate ${args.requiredMenuVisibility} disabled`);
  }

  if (details.length === 0) {
    return `Route access request for ${args.routePath}.`;
  }

  return `Route access request for ${args.routePath}: ${details.join("; ")}.`;
};

// Keep the current organisation scope when recovery actions jump into settings pages,
// otherwise operators can land on the right screen with the wrong tenant context.
const buildScopedSettingsRoute = (args: {
  routePath: string;
  locationSearch: string;
}): string =>
  args.locationSearch
    ? `${args.routePath}${args.locationSearch}`
    : args.routePath;

interface VisibilityEvaluation {
  isEnabled: boolean;
  label: string;
  resolvedFrom: "organisation" | "global" | "default";
}

const formatVisibilityResolutionSource = (
  value: "organisation" | "global" | "default",
): string => {
  switch (value) {
    case "organisation":
      return "organisation";
    case "global":
      return "global";
    case "default":
    default:
      return "default";
  }
};

const buildVisibilityEvaluations = (
  blockedVisibility: AdminV2RevenueMenuVisibilityKey,
  context: AdminV2RevenueAccessContext,
): VisibilityEvaluation[] => {
  switch (blockedVisibility) {
    case "revenue":
      return [
        {
          label: "Revenue workspace feature flag",
          ...context.featureFlags.revenueEnabled,
        },
        {
          label: "Revenue management feature flag",
          ...context.featureFlags.revenueManagementEnabled,
        },
        {
          label: "Revenue control entitlement",
          ...context.entitlements.revenueControlPlane,
        },
      ];
    case "learning":
      return [
        {
          label: "Learning billing feature flag",
          ...context.featureFlags.learningBillingContextEnabled,
        },
        {
          label: "Learning billing entitlement",
          ...context.entitlements.learningBillingContext,
        },
      ];
    case "sales":
      return [
        {
          label: "Sales billing feature flag",
          ...context.featureFlags.salesBillingContextEnabled,
        },
        {
          label: "Sales billing entitlement",
          ...context.entitlements.salesBillingContext,
        },
      ];
    default:
      return [];
  }
};

const buildVisibilityScopeLabel = (args: {
  evaluations: VisibilityEvaluation[];
  routeAccessScopeOrganisationId: string;
}): string | undefined => {
  if (args.routeAccessScopeOrganisationId) {
    return `organisation ${args.routeAccessScopeOrganisationId}`;
  }

  const uniqueSources = [
    ...new Set(args.evaluations.map((evaluation) => evaluation.resolvedFrom)),
  ];

  if (uniqueSources.length === 0) {
    return undefined;
  }

  return uniqueSources
    .map((source) => formatVisibilityResolutionSource(source))
    .join(", ");
};

const buildVisibilityCheckLabels = (args: {
  blockedVisibility: AdminV2RevenueMenuVisibilityKey;
  context: AdminV2RevenueAccessContext | undefined;
}): string[] => {
  if (!args.context) {
    return [];
  }

  return buildVisibilityEvaluations(args.blockedVisibility, args.context)
    .filter((evaluation) => !evaluation.isEnabled)
    .map(
      (evaluation) =>
        `${evaluation.label} (${formatVisibilityResolutionSource(
          evaluation.resolvedFrom,
        )})`,
    );
};

/**
 * Route-level access gate for Admin V2 pages.
 *
 * @remarks
 * This component intentionally supports three modes:
 * 1) `adminE2eBypassEnabled` (local/dev only): do **not** call the backend for access checks.
 *    We still enforce allowlists/menu visibility locally so routes can show realistic
 *    “permission required” UX without coupling to RBAC setup.
 * 2) `adminV2FoundationsEnabled=false` (kill-switch): treat access checks as disabled and
 *    render children. This avoids blocking navigation when Admin V2 APIs are intentionally off.
 * 3) Normal mode: call Admin V2 identity + revenue-access endpoints and gate on their results.
 *
 * When backend checks fail or time out, we surface a correlation id so support can trace the
 * request end-to-end.
 *
 * @see docs/admin/README.md
 * @see docs/LOGGING.md
 * @see docs/guides/PLAIN_LANGUAGE.md
 */
const PermissionProtectedRoute = ({
  children,
  requiredPermission,
  requiredPermissionFallbacks = EMPTY_PERMISSION_FALLBACKS,
  requiredMenuVisibility,
  preserveOpenTemplateEditor = false,
}: PermissionProtectedRouteProps) => {
  const { isDown: healthDown } = useApiHealthRecovery();
  const { user: authUser, loading: authLoading } = useAuthState();
  const [editorRecoveryOwner, setEditorRecoveryOwner] =
    useState<TemplateEditorRecoveryOwner | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [createApproval, createApprovalState] =
    useCreateAdminV2ApprovalMutation();
  const [approvalFlowState, setApprovalFlowState] = useState<{
    approvalFlowError: string | null;
    createdApprovalRequestId: string | null;
    scopeKey: string;
  }>({
    approvalFlowError: null,
    createdApprovalRequestId: null,
    scopeKey: "",
  });
  const [timedOutRouteAccessScopeKey, setTimedOutRouteAccessScopeKey] =
    useState<string | null>(null);
  const [clientFailureCorrelationId] = useState(generateCorrelationId);
  const adminV2FoundationsEnabled = isAdminV2FoundationsEnabled();
  const adminE2eBypassEnabled = isAdminE2eBypassEnabled();
  const e2ePermissionKeys = resolveBypassPermissionKeys(
    getE2eBypassPermissionKeys(),
  );
  const e2eMenuVisibility = resolveBypassMenuVisibility(
    getE2eBypassMenuVisibility(),
  );
  const revenueAccessScope = resolveAdminRevenueAccessScope(location.search);
  const shouldCheckRoutePermission = shouldCheckPermission(
    requiredPermission,
    adminV2FoundationsEnabled,
    adminE2eBypassEnabled,
  );
  // The feature-unavailable gate should still explain who is signed in and what
  // access was resolved, even when route access is blocked only by menu visibility.
  const shouldLoadIdentitySummary =
    adminV2FoundationsEnabled &&
    !adminE2eBypassEnabled &&
    Boolean(requiredPermission ?? requiredMenuVisibility);
  const shouldCheckRouteMenuVisibility = shouldCheckMenuVisibility(
    requiredMenuVisibility,
    adminV2FoundationsEnabled,
    adminE2eBypassEnabled,
  );
  const routePath = location.pathname;
  const routeAccessScopeOrganisationId =
    revenueAccessScope?.organisationId ?? "";
  // Route access state must reset when the semantic gate changes, including organisation-scoped
  // revenue access. Reusing timeout or approval state across org contexts can show the wrong
  // blocking UI for a different tenant, so the scope key includes the validated query scope.
  const routeAccessCheckScopeKey = `${routePath}|${requiredPermission ?? ""}|${requiredPermissionFallbacks.join(",")}|${requiredMenuVisibility ?? ""}|${routeAccessScopeOrganisationId}`;

  const logRouteAccessEvent = useCallback(
    (
      level: "debug" | "info" | "warn",
      message: string,
      metadata: Record<string, unknown>,
    ) => {
      const now = getRouteAccessLogTimestamp();
      for (const [
        cachedSignature,
        timestamp,
      ] of recentRouteAccessEventTimestamps) {
        if (now - timestamp > ROUTE_ACCESS_LOG_DEDUPE_WINDOW_MS) {
          recentRouteAccessEventTimestamps.delete(cachedSignature);
        }
      }

      const signature = JSON.stringify({
        level,
        operation: metadata.operation ?? null,
        routePath: metadata.routePath ?? routePath,
        requiredPermission: metadata.requiredPermission ?? requiredPermission,
        requiredMenuVisibility:
          metadata.requiredMenuVisibility ?? requiredMenuVisibility,
        routeAccessScopeOrganisationId:
          metadata.routeAccessScopeOrganisationId ??
          routeAccessScopeOrganisationId,
        decision: metadata.decision ?? null,
        mode: metadata.mode ?? null,
      });

      const previousTimestamp = recentRouteAccessEventTimestamps.get(signature);
      if (
        typeof previousTimestamp === "number" &&
        now - previousTimestamp < ROUTE_ACCESS_LOG_DEDUPE_WINDOW_MS
      ) {
        return;
      }

      recentRouteAccessEventTimestamps.set(signature, now);
      logger[level](message, metadata);
    },
    [
      requiredMenuVisibility,
      requiredPermission,
      routeAccessScopeOrganisationId,
      routePath,
    ],
  );

  useEffect(() => {
    logRouteAccessEvent("debug", "Evaluating route permission inputs", {
      operation: "route_permission_evaluation",
      routePath,
      requiredPermission: requiredPermission ?? null,
      requiredPermissionFallbacks,
      requiredMenuVisibility: requiredMenuVisibility ?? null,
      routeAccessScopeOrganisationId: routeAccessScopeOrganisationId || null,
      adminV2FoundationsEnabled,
      adminE2eBypassEnabled,
    });
  }, [
    adminE2eBypassEnabled,
    adminV2FoundationsEnabled,
    logRouteAccessEvent,
    requiredMenuVisibility,
    requiredPermission,
    requiredPermissionFallbacks,
    routeAccessScopeOrganisationId,
    routePath,
  ]);

  const {
    data: identityData,
    isError: isIdentityError,
    isFetching: isIdentityFetching,
    error: identityError,
    refetch: refetchIdentity,
  } = useGetAdminV2MeQuery(undefined, {
    skip: !shouldLoadIdentitySummary,
  });
  const {
    data: cachedRevenueAccessData,
    currentData: currentRevenueAccessData,
    isFetching: isRevenueAccessFetching,
    isError: isRevenueAccessError,
    error: revenueAccessError,
    refetch: refetchRevenueAccess,
  } = useGetAdminV2RevenueAccessContextQuery(revenueAccessScope, {
    skip: !shouldCheckRouteMenuVisibility,
  });
  // CRITICAL: RTK Query's data may describe the previous organisation while
  // a new scope loads. Only currentData may authorize the new scope.
  // https://redux-toolkit.js.org/rtk-query/usage/queries#query-loading-state
  const revenueAccessData = isRevenueAccessFetching
    ? currentRevenueAccessData
    : (currentRevenueAccessData ?? cachedRevenueAccessData);
  const identityActorMismatch = Boolean(
    shouldCheckRoutePermission &&
    identityData &&
    !authLoading &&
    (!authUser?.uid || identityData.staffUser.firebaseUid !== authUser.uid),
  );
  const hasVisibilityAccessCheckFailed =
    shouldCheckRouteMenuVisibility && isRevenueAccessError;
  // CRITICAL PD-51: retain only an already-authorized actor and route scope.
  // A registered template editor additionally requires its bound owner;
  // confirmed denials and a new organisation remain gated.
  // https://react.dev/learn/preserving-and-resetting-state#state-is-tied-to-a-position-in-the-render-tree
  const isTemplateWorkspace =
    preserveOpenTemplateEditor || routePath === "/email-comms/templates";
  const eligibleTemplateEditor = Boolean(
    preserveOpenTemplateEditor &&
    !shouldCheckRouteMenuVisibility &&
    !authLoading &&
    authUser?.uid &&
    editorRecoveryOwner?.uid === authUser.uid &&
    identityData?.staffUser.firebaseUid === authUser.uid &&
    identityData.staffUser.id === editorRecoveryOwner.staffId &&
    identityData.staffUser.isActive &&
    identityData.permissionKeys.includes("messaging.manage_templates") &&
    hasRequiredRoutePermission(
      identityData.permissionKeys,
      requiredPermission,
      requiredPermissionFallbacks,
    ),
  );
  const eligibleOtherPage = Boolean(
    !(
      isTemplateWorkspace &&
      preserveOpenTemplateEditor &&
      editorRecoveryOwner
    ) &&
    shouldCheckRoutePermission &&
    !authLoading &&
    authUser?.uid &&
    identityData?.staffUser.firebaseUid === authUser.uid &&
    identityData.staffUser.id &&
    identityData.staffUser.isActive &&
    hasRequiredRoutePermission(
      identityData.permissionKeys,
      requiredPermission,
      requiredPermissionFallbacks,
    ) &&
    (!shouldCheckRouteMenuVisibility ||
      (!isRevenueAccessError &&
        revenueAccessData &&
        requiredMenuVisibility &&
        isRevenueMenuVisibilityEnabled(
          revenueAccessData.menuVisibility,
          requiredMenuVisibility,
        ))),
  );
  const eligibleForReconnect = eligibleTemplateEditor || eligibleOtherPage;
  const identityReconnect = useIdentityReconnect({
    scopeKey: eligibleForReconnect
      ? JSON.stringify([
          authUser?.uid,
          identityData?.staffUser.id,
          routeAccessCheckScopeKey,
        ])
      : null,
    allowedNow: eligibleForReconnect,
    isError: Boolean(isIdentityError),
    isFetching: Boolean(isIdentityFetching),
    error: identityError,
    refetch: refetchIdentity,
  });
  const hasIdentityAccessCheckFailed =
    shouldCheckRoutePermission &&
    (identityActorMismatch ||
      (isIdentityError && !identityReconnect.retaining));
  const isIdentityAccessCheckPending =
    shouldCheckRoutePermission &&
    (authLoading || (!isIdentityError && !identityData));
  const isVisibilityAccessCheckPending =
    shouldCheckRouteMenuVisibility &&
    !isRevenueAccessError &&
    !revenueAccessData;
  const hasPendingAccessChecks =
    isIdentityAccessCheckPending || isVisibilityAccessCheckPending;
  const hasRouteAccessCheckTimedOut =
    timedOutRouteAccessScopeKey === routeAccessCheckScopeKey;
  const createdApprovalRequestId =
    approvalFlowState.scopeKey === routeAccessCheckScopeKey
      ? approvalFlowState.createdApprovalRequestId
      : null;
  const approvalFlowError =
    approvalFlowState.scopeKey === routeAccessCheckScopeKey
      ? approvalFlowState.approvalFlowError
      : null;

  useEffect(() => {
    if (!hasPendingAccessChecks || hasRouteAccessCheckTimedOut) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      logger.warn("Route access checks timed out before completion", {
        operation: "route_access_check_timeout",
        routePath,
        requiredPermission: requiredPermission ?? null,
        requiredMenuVisibility: requiredMenuVisibility ?? null,
        routeAccessScopeOrganisationId: routeAccessScopeOrganisationId || null,
      });
      setTimedOutRouteAccessScopeKey((current) =>
        current === routeAccessCheckScopeKey
          ? current
          : routeAccessCheckScopeKey,
      );
    }, ROUTE_ACCESS_CHECK_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    hasRouteAccessCheckTimedOut,
    hasPendingAccessChecks,
    routeAccessCheckScopeKey,
    routeAccessScopeOrganisationId,
    requiredMenuVisibility,
    requiredPermission,
    routePath,
  ]);

  if (!requiredPermission && !requiredMenuVisibility) {
    return <>{children}</>;
  }

  const isApprovalsQueueSelfBlocked =
    routePath === APPROVALS_QUEUE_ROUTE_PATH &&
    requiredPermission === "approvals.view";

  const handleRequestAccess = async () => {
    try {
      setApprovalFlowState({
        approvalFlowError: null,
        createdApprovalRequestId: null,
        scopeKey: routeAccessCheckScopeKey,
      });
      const response = await createApproval({
        requestType: ROUTE_ACCESS_APPROVAL_REQUEST_TYPE,
        reason: buildRouteAccessReason({
          routePath,
          requiredPermission,
          requiredMenuVisibility,
        }),
        targetType: ROUTE_ACCESS_APPROVAL_TARGET_TYPE,
        targetId: routePath,
        requiredPermissionKey: requiredPermission,
        payload: {
          routePath,
          requiredPermission: requiredPermission ?? null,
          requiredMenuVisibility: requiredMenuVisibility ?? null,
        },
      }).unwrap();
      setApprovalFlowState({
        approvalFlowError: null,
        createdApprovalRequestId: response.approval.id,
        scopeKey: routeAccessCheckScopeKey,
      });
      return;
    } catch (error) {
      const approvalError = formatApiError(error);
      logger.error(
        "Failed to create admin route access approval request",
        error instanceof Error ? error : undefined,
        {
          operation: "admin_v2_route_access_request_failed",
          routePath,
          requiredPermission: requiredPermission ?? null,
          requiredMenuVisibility: requiredMenuVisibility ?? null,
          ...(approvalError.correlation
            ? { correlationId: approvalError.correlation }
            : {}),
          ...(approvalError.errorCode
            ? { errorCode: approvalError.errorCode }
            : {}),
        },
      );
      setApprovalFlowState({
        approvalFlowError: adminUiCopy.authAccess.approvalCreateFailed,
        createdApprovalRequestId: null,
        scopeKey: routeAccessCheckScopeKey,
      });
    }
  };

  const handleOpenApprovals = () => {
    void navigate(APPROVALS_QUEUE_ROUTE_PATH);
  };
  const handleOpenFeatureFlags = () => {
    void navigate(
      buildScopedSettingsRoute({
        routePath: FEATURE_FLAGS_ROUTE_PATH,
        locationSearch: location.search,
      }),
    );
  };
  const handleOpenEntitlements = () => {
    void navigate(
      buildScopedSettingsRoute({
        routePath: ENTITLEMENTS_ROUTE_PATH,
        locationSearch: location.search,
      }),
    );
  };

  const permissionDeniedActions = (
    <div className="vnext-card__body">
      {isApprovalsQueueSelfBlocked ? (
        <VNextNotice tone="warning">
          {adminUiCopy.authAccess.approvalsQueueAccessHelp}
        </VNextNotice>
      ) : null}
      {createdApprovalRequestId ? (
        <VNextNotice tone="info">
          Access request <code>{createdApprovalRequestId}</code> has been
          created.
        </VNextNotice>
      ) : null}
      {approvalFlowError ? (
        <VNextNotice tone="danger">{approvalFlowError}</VNextNotice>
      ) : null}
      {isApprovalsQueueSelfBlocked ? null : (
        <div className="vnext-inline-actions">
          <VNextButton
            disabled={createApprovalState.isLoading}
            onClick={() => {
              void handleRequestAccess();
            }}
            tone="secondary"
          >
            {adminUiCopy.common.requestAccess}
          </VNextButton>
          <VNextButton
            onClick={() => {
              handleOpenApprovals();
            }}
            tone="quiet"
          >
            {adminUiCopy.common.openApprovalsQueue}
          </VNextButton>
        </div>
      )}
    </div>
  );

  const currentUserName = formatAvailabilityText(
    identityData?.staffUser.displayName ?? authUser?.displayName,
  );
  const currentUserEmail = formatAvailabilityText(
    identityData?.staffUser.email ?? authUser?.email,
  );
  const currentUserUid = formatAvailabilityText(
    identityData?.staffUser.firebaseUid ?? authUser?.uid,
  );
  const currentUserStatus = identityData?.staffUser.isActive
    ? "Active"
    : identityData?.staffUser
      ? "Inactive"
      : "Status unavailable";
  const roleLabels = formatRoleLabels({
    roleNames: identityData?.staffUser.roles.map((role) => role.roleName) ?? [],
    bootstrapRoleAssigned:
      identityData?.staffUser.bootstrapRoleAssigned ?? false,
    adminE2eBypassEnabled,
  });
  const permissionKeysForSummary = adminE2eBypassEnabled
    ? [...e2ePermissionKeys].sort((left, right) => left.localeCompare(right))
    : [...(identityData?.permissionKeys ?? [])].sort((left, right) =>
        left.localeCompare(right),
      );
  const canManageFeatureFlags = permissionKeysForSummary.includes(
    "featureflags.manage",
  );
  const canManageEntitlements = permissionKeysForSummary.includes(
    "entitlements.manage",
  );
  const visibleAccessLabels = adminE2eBypassEnabled
    ? formatVisibleAccessLabels(e2eMenuVisibility)
    : formatVisibleAccessLabels(revenueAccessData?.menuVisibility ?? {});

  const accessDeniedView = (missingPermission: string) => {
    const missingPermissionLabel =
      formatAdminPermissionLabel(missingPermission);

    return (
      <RouteGatePageShell
        subtitle={adminUiCopy.routeAccess.permissionDeniedSubtitle}
        title={adminUiCopy.routeAccess.permissionDeniedTitle}
      >
        <RouteGateShellSurface>
          <RouteGateCard
            alertMessage={adminUiCopy.routeAccess.permissionDeniedMessage}
            alertSeverity="warning"
            embedded
            maxWidth={720}
            title={adminUiCopy.routeAccess.permissionDeniedTitle}
          >
            <p className="vnext-gate-copy">
              Permission needed: {missingPermissionLabel}
            </p>
            {permissionDeniedActions}
          </RouteGateCard>
        </RouteGateShellSurface>
      </RouteGatePageShell>
    );
  };

  const menuVisibilityDeniedView = (
    blockedVisibility: AdminV2RevenueMenuVisibilityKey,
  ) => {
    const visibilityLabel = MENU_VISIBILITY_COPY_LABELS[blockedVisibility];
    const visibilityTitle =
      visibilityLabel.charAt(0).toUpperCase() + visibilityLabel.slice(1);
    const visibilityCheckLabels = buildVisibilityCheckLabels({
      blockedVisibility,
      context: revenueAccessData?.context,
    });
    const visibilityScopeLabel = buildVisibilityScopeLabel({
      evaluations: revenueAccessData?.context
        ? buildVisibilityEvaluations(
            blockedVisibility,
            revenueAccessData.context,
          )
        : [],
      routeAccessScopeOrganisationId,
    });
    const visibilityResolutionActions =
      canManageFeatureFlags || canManageEntitlements ? (
        <div className="vnext-inline-actions">
          {canManageFeatureFlags ? (
            <VNextButton
              onClick={() => {
                handleOpenFeatureFlags();
              }}
              tone="secondary"
            >
              {adminUiCopy.common.openFeatureFlags}
            </VNextButton>
          ) : null}
          {canManageEntitlements ? (
            <VNextButton
              onClick={() => {
                handleOpenEntitlements();
              }}
              tone="quiet"
            >
              {adminUiCopy.common.openEntitlements}
            </VNextButton>
          ) : null}
        </div>
      ) : null;

    return (
      <RouteGatePageShell
        subtitle={adminUiCopy.routeAccess.visibilityDeniedSubtitle}
        title={adminUiCopy.routeAccess.visibilityDeniedTitle}
      >
        <RouteGateShellSurface>
          <RouteGateCard
            title={adminUiCopy.routeAccess.visibilityDeniedTitle}
            alertSeverity="warning"
            alertMessage={`${visibilityLabel} access is currently hidden for this account.`}
            embedded
          >
            <div aria-live="polite" className="vnext-card__body" role="status">
              <p>
                {visibilityTitle} workspace access is hidden by visibility
                controls.
              </p>
              <p>{adminUiCopy.routeAccess.visibilityReason}</p>
              <p>
                {canManageFeatureFlags || canManageEntitlements
                  ? adminUiCopy.routeAccess.visibilityHelpManage
                  : adminUiCopy.routeAccess.visibilityHelpRequest}
              </p>
              <p className="vnext-muted">
                Visibility control: <code>{blockedVisibility}</code>
              </p>
              {visibilityScopeLabel ? (
                <p className="vnext-muted">
                  Visibility scope: <code>{visibilityScopeLabel}</code>
                </p>
              ) : null}
              {visibilityCheckLabels.length > 0 ? (
                <p className="vnext-muted">
                  Hidden because: {visibilityCheckLabels.join(", ")}
                </p>
              ) : null}
            </div>
            <RouteGateAccessSummary
              currentUserName={currentUserName}
              currentUserEmail={currentUserEmail}
              currentUserUid={currentUserUid}
              currentUserStatus={currentUserStatus}
              roleLabels={roleLabels}
              permissionKeys={permissionKeysForSummary}
              visibleAccessLabels={visibleAccessLabels}
              blockedVisibility={blockedVisibility}
              visibilityScopeLabel={visibilityScopeLabel}
              visibilityCheckLabels={visibilityCheckLabels}
              adminE2eBypassEnabled={adminE2eBypassEnabled}
            />
            {visibilityResolutionActions}
          </RouteGateCard>
        </RouteGateShellSurface>
      </RouteGatePageShell>
    );
  };

  if (adminE2eBypassEnabled) {
    // Important: bypass mode skips backend checks, but still applies local allowlists/visibility
    // so we don't accidentally treat "bypass" as "everything is always allowed".
    if (
      requiredPermission &&
      !hasRequiredRoutePermission(
        e2ePermissionKeys,
        requiredPermission,
        requiredPermissionFallbacks,
      )
    ) {
      logRouteAccessEvent(
        "warn",
        "Route denied due to missing bypass permission key",
        {
          operation: "route_access_denied_permission",
          routePath,
          requiredPermission,
          mode: "e2e_bypass",
        },
      );
      return accessDeniedView(requiredPermission);
    }

    if (
      requiredMenuVisibility &&
      !isRevenueMenuVisibilityEnabled(e2eMenuVisibility, requiredMenuVisibility)
    ) {
      logRouteAccessEvent(
        "warn",
        "Route denied due to bypass menu visibility gate",
        {
          operation: "route_access_denied_visibility",
          routePath,
          requiredMenuVisibility,
          mode: "e2e_bypass",
        },
      );
      return menuVisibilityDeniedView(requiredMenuVisibility);
    }

    logRouteAccessEvent("info", "Route access decision: allow", {
      operation: "route_access_decision",
      routePath,
      mode: "e2e_bypass",
      decision: "allow",
    });
    return <>{children}</>;
  }

  if (!adminV2FoundationsEnabled) {
    return <>{children}</>;
  }

  if (
    hasIdentityAccessCheckFailed ||
    hasVisibilityAccessCheckFailed ||
    (hasRouteAccessCheckTimedOut && hasPendingAccessChecks)
  ) {
    // CRITICAL (SLK-03 definite 403): a definite identity 403 renders ONE
    // permanent denial (no Retry, no reconnect/limited-mode banner, no
    // visibility detail) while keeping the code and correlation ID. Transient
    // failures keep the existing retry copy. See isForbiddenIdentityError.
    const identityForbidden =
      hasIdentityAccessCheckFailed && isForbiddenIdentityError(identityError);
    const accessCheckFailure = buildRouteAccessCheckFailureDetails({
      hasTimedOut: hasRouteAccessCheckTimedOut && !identityForbidden,
      isIdentityAccessCheckPending,
      isVisibilityAccessCheckPending,
      identityError,
      visibilityError: identityForbidden ? undefined : revenueAccessError,
    });
    const effectiveErrorCode =
      accessCheckFailure.errorCode || ROUTE_ACCESS_CHECK_FAILED_ERROR_CODE;

    return (
      <RouteGatePageShell
        hideAccessDataErrorBanner={identityForbidden}
        subtitle={
          identityForbidden ? undefined : adminUiCopy.routeAccess.subtitle
        }
        title={
          identityForbidden
            ? adminUiCopy.routeAccess.deniedTitle
            : adminUiCopy.routeAccess.title
        }
      >
        <RouteGateShellSurface>
          <RouteGateCard
            title={
              identityForbidden
                ? adminUiCopy.routeAccess.deniedTitle
                : adminUiCopy.routeAccess.title
            }
            alertSeverity="error"
            alertMessage={
              identityForbidden
                ? adminUiCopy.routeAccess.deniedMessage
                : hasRouteAccessCheckTimedOut
                  ? adminUiCopy.routeAccess.timedOutMessage
                  : adminUiCopy.routeAccess.unavailableMessage
            }
            embedded
            maxWidth={720}
          >
            <div className="vnext-card__body">
              {identityForbidden ? null : (
                <>
                  <p className="vnext-muted">
                    Identity check: {accessCheckFailure.identityMessage}
                  </p>
                  <p className="vnext-muted">
                    Visibility check: {accessCheckFailure.visibilityMessage}
                  </p>
                </>
              )}
              <p className="vnext-muted">
                {adminUiCopy.common.errorCodeLabel}:{" "}
                <code>{effectiveErrorCode}</code>
              </p>
              {accessCheckFailure.correlationId ? (
                <p className="vnext-muted">
                  {adminUiCopy.common.correlationIdLabel}:{" "}
                  <code>{accessCheckFailure.correlationId}</code>
                </p>
              ) : null}
              <p className="vnext-muted">
                {adminUiCopy.routeAccess.supportHelp}
              </p>
              {identityForbidden ? null : (
                <div className="vnext-inline-actions">
                  {(!authUser?.uid && identityActorMismatch) ||
                  (isIdentityError &&
                    !identityActorMismatch &&
                    isDefiniteIdentitySessionError(identityError)) ? (
                    <IdentitySessionSignInButton />
                  ) : (
                    <VNextButton
                      onClick={() => {
                        setTimedOutRouteAccessScopeKey(null);
                        if (shouldLoadIdentitySummary) void refetchIdentity();
                        if (shouldCheckRouteMenuVisibility)
                          void refetchRevenueAccess();
                      }}
                      tone="secondary"
                    >
                      {adminUiCopy.routeAccess.retryChecks}
                    </VNextButton>
                  )}
                </div>
              )}
            </div>
          </RouteGateCard>
        </RouteGateShellSurface>
      </RouteGatePageShell>
    );
  }

  // A retry can be fetching without RTK's first-load isLoading flag. No
  // authority data still means pending, never permission to render children.
  if (hasPendingAccessChecks) {
    return loadingView;
  }

  if (
    requiredPermission &&
    identityData &&
    !hasRequiredRoutePermission(
      identityData.permissionKeys,
      requiredPermission,
      requiredPermissionFallbacks,
    )
  ) {
    logRouteAccessEvent(
      "warn",
      "Route denied due to missing required permission",
      {
        operation: "route_access_denied_permission",
        routePath,
        requiredPermission,
      },
    );
    return accessDeniedView(requiredPermission);
  }

  if (
    requiredMenuVisibility &&
    revenueAccessData &&
    !isRevenueMenuVisibilityEnabled(
      revenueAccessData.menuVisibility,
      requiredMenuVisibility,
    )
  ) {
    logRouteAccessEvent("warn", "Route denied due to menu visibility gate", {
      operation: "route_access_denied_visibility",
      routePath,
      requiredMenuVisibility,
    });
    return menuVisibilityDeniedView(requiredMenuVisibility);
  }

  logRouteAccessEvent("info", "Route access decision: allow", {
    operation: "route_access_decision",
    routePath,
    decision: "allow",
  });
  const reconnectError = formatApiError(identityReconnect.error);
  const reconnectNotice =
    !eligibleTemplateEditor &&
    identityReconnect.retaining &&
    !(healthDown && isOutageRelatedAccessFailure(identityReconnect.error)) ? (
      <VNextNotice role="status" tone="warning">
        <span>
          {identityReconnect.reconnecting
            ? "Reconnecting… Your work is still available."
            : "We couldn't check access. Your work is still available."}
        </span>{" "}
        <span>
          {adminUiCopy.common.errorCodeLabel}:{" "}
          <code>
            {reconnectError.errorCode ?? ROUTE_ACCESS_CHECK_FAILED_ERROR_CODE}
          </code>
        </span>{" "}
        <span>
          {adminUiCopy.common.correlationIdLabel}:{" "}
          <code>
            {reconnectError.correlation ?? clientFailureCorrelationId}
          </code>
        </span>{" "}
        {identityReconnect.retryOffered ? (
          <VNextButton
            disabled={!identityReconnect.retryAllowed}
            onClick={identityReconnect.retryNow}
            tone="secondary"
          >
            {adminUiCopy.routeAccess.retryChecks}
          </VNextButton>
        ) : null}
      </VNextNotice>
    ) : null;
  return (
    <TemplateEditorRecoveryContext.Provider value={setEditorRecoveryOwner}>
      <IdentityReconnectContext.Provider
        value={identityReconnect.retaining ? identityReconnect : null}
      >
        <RecoveryNoticesProvider
          notice={
            reconnectNotice
              ? { id: "identity", content: reconnectNotice }
              : null
          }
        >
          {children}
          {reconnectNotice}
        </RecoveryNoticesProvider>
      </IdentityReconnectContext.Provider>
    </TemplateEditorRecoveryContext.Provider>
  );
};

export default PermissionProtectedRoute;
